// 質問データに関するユーティリティ関数群
// 解析や集計処理を共通化することで、PC版とモバイル版の双方で同じロジックを利用できるようにする

// 選択肢型の値を配列に正規化する関数
export const parseSelectableValues = (rawValue) => {
  if (rawValue === null || rawValue === undefined) {
    return [];
  }

  if (Array.isArray(rawValue)) {
    return rawValue
      .flatMap((value) => parseSelectableValues(value))
      .map((value) => value.toString())
      .filter(Boolean);
  }

  if (typeof rawValue === 'object') {
    return Object.values(rawValue || {})
      .flatMap((value) => parseSelectableValues(value))
      .map((value) => value.toString())
      .filter(Boolean);
  }

  const normalized = rawValue.toString().trim();
  if (!normalized) {
    return [];
  }

  try {
    const parsed = JSON.parse(normalized);
    if (parsed && (Array.isArray(parsed) || typeof parsed === 'object')) {
      return parseSelectableValues(parsed);
    }
  } catch (error) {
    // JSON として解釈できない場合は、そのまま文字列として扱う
  }

  if (normalized.includes('\n')) {
    return normalized
      .split(/\r?\n/)
      .map((part) => part.trim())
      .filter(Boolean);
  }

  if (normalized.includes('|')) {
    return normalized
      .split('|')
      .map((part) => part.trim())
      .filter(Boolean);
  }

  return [normalized];
};

// 取得した配列をソート済みのユニークなリストに変換する関数
export const toSortedUniqueList = (values = []) => (
  Array.from(new Set(values.filter(Boolean).map((value) => value.toString().trim())))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, 'ja', { numeric: true }))
);

// 進捗データの集計ロジックをまとめた関数
export const calculateProgressData = (questionData = [], resultMap = {}, categoryNames = [], taskNames = []) => {
  if (!questionData || questionData.length === 0) {
    return null;
  }

  // 合計値を保持するオブジェクト
  // 「除外」ステータスも集計できるように初期値へ追加
  const summaryStats = { clear: 0, error: 0, unanswered: 0, excluded: 0 };

  // カテゴリ・タスクごとの統計を保持するマップ
  const categoryStats = new Map();
  const taskStats = new Map();

  // 統計情報の初期化と加算を担うヘルパー
  const ensureStats = (map, key) => {
    if (!map.has(key)) {
      // 各分類でも「除外」を含む全ステータスをカウントできるようにする
      map.set(key, { clear: 0, error: 0, unanswered: 0, excluded: 0 });
    }
    return map.get(key);
  };

  const registerStats = (map, key, resultKey) => {
    const stats = ensureStats(map, key);
    stats[resultKey] += 1;
  };

  // 進捗集計用に結果を正規化するヘルパー
  const resolveResultValue = (rawValue) => {
    if (typeof rawValue === 'object') {
      return rawValue?.result;
    }
    return rawValue;
  };

  const getResultKey = (resultValue) => {
    const resolved = resolveResultValue(resultValue);

    // 新しいステータスコード（correct / incorrect / excluded）に対応する
    if (resolved === 'correct') {
      return 'clear';
    }
    if (resolved === 'incorrect') {
      return 'error';
    }
    if (resolved === 'excluded') {
      return 'excluded';
    }
    return 'unanswered';
  };

  questionData.forEach((question) => {
    const resultKey = getResultKey(resultMap[question.id]);
    summaryStats[resultKey] += 1;

    // 新しい正規化カラムに合わせてカテゴリ・タスクを集計する
    const categories = parseSelectableValues(question?.category);
    const categoryTargets = categories.length > 0 ? categories : ['未分類'];
    categoryTargets.forEach((category) => registerStats(categoryStats, category, resultKey));

    const tasks = parseSelectableValues(question?.task);
    const taskTargets = tasks.length > 0 ? tasks : ['未分類'];
    taskTargets.forEach((task) => registerStats(taskStats, task, resultKey));
  });

  const toEntry = (name, stats = { clear: 0, error: 0, unanswered: 0, excluded: 0 }) => {
    const clear = stats.clear || 0;
    const error = stats.error || 0;
    const unanswered = stats.unanswered || 0;
    const excluded = stats.excluded || 0;
    const total = clear + error + unanswered + excluded;

    return {
      name,
      clear,
      error,
      unanswered,
      excluded,
      totalQuestions: total,
      clearProgress: total > 0 ? (clear / total) * 100 : 0,
      errorProgress: total > 0 ? (error / total) * 100 : 0,
      unansweredProgress: total > 0 ? (unanswered / total) * 100 : 0,
      excludedProgress: total > 0 ? (excluded / total) * 100 : 0,
    };
  };

  const sortByOrder = (orderedList = [], map) => {
    const entries = [];

    orderedList.forEach((name) => {
      entries.push(toEntry(name, map.get(name)));
    });

    const extraNames = Array.from(map.keys()).filter((name) => !orderedList.includes(name));
    extraNames.sort((a, b) => a.localeCompare(b, 'ja', { numeric: true }));
    extraNames.forEach((name) => {
      entries.push(toEntry(name, map.get(name)));
    });

    return entries;
  };

  const summaryEntry = toEntry('すべて', summaryStats);
  const categoryBreakdown = sortByOrder(categoryNames, categoryStats);
  const taskBreakdown = sortByOrder(taskNames, taskStats);

  return {
    clear: summaryEntry.clear,
    error: summaryEntry.error,
    unanswered: summaryEntry.unanswered,
    excluded: summaryEntry.excluded,
    totalQuestions: summaryEntry.totalQuestions,
    clearProgress: summaryEntry.clearProgress,
    errorProgress: summaryEntry.errorProgress,
    unansweredProgress: summaryEntry.unansweredProgress,
    excludedProgress: summaryEntry.excludedProgress,
    categoryBreakdown,
    taskBreakdown,
  };
};
